<template>
  <div class="match-summary" :class="{ 'sidebar-open': !sidebarCollapsed, 'sidebar-collapsed': sidebarCollapsed }">
    <div class="summary-header">
      <div class="summary-header-left">
        <div class="set-tabs">
          <button
            class="btn tab-btn"
            :class="{ active: activeSetId === 'all' }"
            @click="activeSetId = 'all'"
          >
            All-in-one
          </button>
          <button
            class="btn tab-btn"
            :class="{ active: activeSetId === 'total' }"
            @click="activeSetId = 'total'"
          >
            Totals
          </button>
          <button
            v-for="set in displaySets"
            :key="set.id"
            class="btn tab-btn"
            :class="{ active: activeSetId === set.id, 'non-participant': !playerParticipatedInSet(set) }"
            @click="activeSetId = set.id"
          >
            Set {{ set.id }}
          </button>
        </div>
      </div>
      <div class="summary-header-right">
        <button class="btn primary" @click="$refs.allPlayersTable.exportMatchStats()">
          Export Stats
        </button>
        <button
          class="btn icon-btn"
          :class="{ active: activeSetId === 'info' }"
          @click="activeSetId = 'info'"
          title="Match Info"
        ><span style="font-size: 1.4em; line-height: 1;">⚙</span></button>
        <button class="btn destructive" @click="$emit('close')">
          Close Summary
        </button>
      </div>
    </div>
    <div class="summary-content">
      <div v-if="activeSetId === 'info'" class="match-info-editor">
        <h3>Match Information</h3>
        <div class="info-form">
          <label>
            <span class="label-text">Our Team Name</span>
            <input
              type="text"
              v-model="matchMetadata.teamName"
              @input="updateMetadata"
              placeholder="Moss Side Jaguars"
            />
          </label>
          <label>
            <span class="label-text">Opponent</span>
            <input
              type="text"
              v-model="matchMetadata.opposition"
              @input="updateMetadata"
              placeholder="Enter opponent name"
            />
          </label>
          <label>
            <span class="label-text">Date</span>
            <input
              type="date"
              v-model="matchMetadata.date"
              @input="updateMetadata"
            />
          </label>
          <label>
            <span class="label-text">Location</span>
            <select v-model="matchMetadata.homeAway" @change="updateMetadata">
              <option value="">Select</option>
              <option value="Home">Home</option>
              <option value="Away">Away</option>
            </select>
          </label>
          <label>
            <span class="label-text">Division</span>
            <input
              type="text"
              v-model="matchMetadata.competition"
              @input="updateMetadata"
              placeholder="Enter division/competition"
            />
          </label>
          <label>
            <span class="label-text">Round</span>
            <input
              type="text"
              v-model="matchMetadata.round"
              @input="updateMetadata"
              placeholder="e.g. Round 5, Matchday 3"
            />
          </label>
          <label>
            <span class="label-text">Venue</span>
            <input
              type="text"
              v-model="matchMetadata.venue"
              @input="updateMetadata"
              placeholder="Enter venue name"
            />
          </label>
        </div>
      </div>
      <div v-if="activeSetId !== 'info'" class="summary-view-tabs">
        <button
          class="btn tab-btn"
          :class="{ active: activeView === 'stats' }"
          @click="activeView = 'stats'"
        >
          Team vs Opp
        </button>
        <button
          class="btn tab-btn"
          :class="{ active: activeView === 'detail' }"
          @click="activeView = 'detail'"
        >
          Stats
        </button>
        <button
          class="btn tab-btn"
          :class="{ active: activeView === 'players' }"
          @click="activeView = 'players'"
        >
          All Player Stats
        </button>
        <button
          v-if="activeSetId !== 'total' && activeSetId !== 'all'"
          class="btn tab-btn"
          :class="{ active: activeView === 'play' }"
          @click="activeView = 'play'"
        >
          Play-By-Play
        </button>
      </div>
      <div v-if="activeSetId !== 'info' && activeView === 'detail'" class="stats-filter-bar">
        <div class="summary-filter">
          <select v-model="selectedPlayerNumber" class="filter-select">
            <option :value="null">Team</option>
            <option v-for="player in playerOptions" :key="player.number" :value="player.number">
              {{ player.label }}
            </option>
          </select>
        </div>
        <button
          v-for="tab in statsTabs"
          :key="tab"
          class="btn tab-btn"
          :class="{ active: activeStatsTab === tab }"
          @click="activeStatsTab = tab"
        >{{ tab }}</button>
      </div>
      <AllPlayersStatsTable
        v-show="activeView === 'players'"
        ref="allPlayersTable"
        :points="activePoints"
        :roster="playerRoster"
        :sets="displaySets"
        :active-set-id="activeSetId"
        @update:selectedPlayerNumber="allPlayersSelectedNumber = $event"
      />
      <PlayByPlayGrid
        v-if="activeView !== 'players' && activeSetId !== 'total' && activeSetId !== 'all' && activeSetId !== 'info' && activeView === 'play' && activeSet"
        :playByPlay="activeSet.points"
        :current-lineup="activeSet.startingLineup || currentLineup"
      />
      <StatsDisplay
        v-else-if="activeView !== 'players' && activeView === 'detail' && activeSetId !== 'info' && activePoints.length"
        inline
        :tab="activeStatsTab"
        :points="activePoints"
        :selected-player-number="selectedPlayerNumber"
        :active-set-id="activeSetId"
        :sets="activeSetId === 'all' ? displaySets : null"
        :player-participated="selectedPlayerNumber && activeSetId !== 'all' ? playerParticipatedInSet(activeSet) : null"
      />
      <StatsDisplay
        v-else-if="activeView !== 'players' && activeSetId !== 'info' && activePoints.length"
        summary-mode
        :points="activePoints"
        :active-set-id="activeSetId === 'all' ? 'total' : activeSetId"
        :sets="activeSetId === 'all' ? displaySets : null"
      />
      <p v-else-if="activeView !== 'players' && activeSetId !== 'info'" class="empty-text">No points yet.</p>
    </div>
  </div>
</template>

<script>
import { store } from '../store.js';
import PlayByPlayGrid from './PlayByPlayGrid.vue';
import StatsDisplay from './StatsDisplay.vue';
import AllPlayersStatsTable from './AllPlayersStatsTable.vue';

export default {
  name: 'MatchSummary',
  components: {
    PlayByPlayGrid,
    StatsDisplay,
    AllPlayersStatsTable
  },
  props: {
    currentLineup: {
      type: Array,
      required: true
    },
    sidebarCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeSetId: null,
      allPlayersSelectedNumber: null,
      activeView: 'stats',
      activeStatsTab: 'All',
      statsTabs: ['All', 'Serve', 'Receive', 'Attack', 'Block', 'Defence', 'Other Errors'],
      selectedPlayerNumber: null,
      matchMetadata: {
        teamName: 'Moss Side Jaguars',
        date: '',
        homeAway: '',
        opposition: '',
        competition: '',
        venue: '',
        round: ''
      }
    };
  },
  computed: {
    matchFinished() {
      return store.match.finished;
    },
    displaySets() {
      return store.match.sets || [];
    },
    playerOptions() {
      const roster = Array.isArray(store.match.roster) ? store.match.roster : [];
      const seen = new Set();
      return roster
        .filter(player => player && player.number !== undefined)
        .filter(player => {
          if (seen.has(player.number)) {
            return false;
          }
          seen.add(player.number);
          return true;
        })
        .map(player => ({
          number: player.number,
          label: `${player.number} - ${player.name || ''}`.trim()
        }))
        .sort((a, b) => a.number - b.number);
    },
    playerRoster() {
      const roster = Array.isArray(store.match.roster) ? store.match.roster : [];
      const seen = new Set();
      return roster
        .filter(player => player && player.number !== undefined)
        .filter(player => {
          if (seen.has(player.number)) {
            return false;
          }
          seen.add(player.number);
          return true;
        })
        .sort((a, b) => a.number - b.number);
    },
    activeSet() {
      if (this.activeSetId === 'total' || this.activeSetId === 'all') {
        return null;
      }
      return this.displaySets.find(set => set.id === this.activeSetId) || this.displaySets[0];
    },
    activePoints() {
      if (this.activeSetId === 'total' || this.activeSetId === 'all') {
        return this.displaySets.flatMap(set => set.points || []);
      }
      return this.activeSet ? this.activeSet.points : [];
    }
  },
  watch: {
    activeSetId(newId) {
      if ((newId === 'total' || newId === 'all') && this.activeView === 'play') {
        this.activeView = 'stats';
      }
    }
  },
  methods: {
    updateMetadata() {
      store.setMatchMetadata(this.matchMetadata);
    },
    playerParticipatedInSet(set) {
      const num = this.selectedPlayerNumber || this.allPlayersSelectedNumber;
      if (!num || !set) return true;
      const lineup = Array.isArray(set.startingLineup) ? set.startingLineup : [];
      if (lineup.some(item => item.player?.number === num)) return true;
      if (set.startingLibero?.number === num) return true;
      const points = Array.isArray(set.points) ? set.points : [];
      return points.some(p => (p.currentLineupNumbers || []).includes(num));
    }
  },
  mounted() {
    this.activeSetId = 'all';
    // Initialize metadata from store with sanitization - ensure teamName defaults to 'Moss Side Jaguars' if empty
    const metadata = store.match.metadata;
    this.matchMetadata = {
      teamName: metadata.teamName || 'Moss Side Jaguars',
      date: metadata.date || '',
      homeAway: metadata.homeAway || '',
      opposition: metadata.opposition || '',
      competition: metadata.competition || '',
      venue: metadata.venue || '',
      round: metadata.round || ''
    };
  }
};
</script>

<style lang="scss">
.match-summary {
  position: fixed;
  inset: 0;
  margin: 10px;
  right: 380px;
  border-radius: 12px;
  background: var(--grey-5);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: right 0.3s ease;

  &.sidebar-open {
    right: 380px;
  }

  &.sidebar-collapsed {
    right: 40px;
  }
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid var(--grey-25);
  background: var(--grey-5);

  .summary-header-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
  }
}

.set-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.summary-filter {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-label {
  font-size: 12px;
  color: var(--grey-600);
  font-weight: 600;
}

.filter-select {
  height: 34px;
  min-width: 160px;
  border: 1px solid var(--grey-75);
  border-radius: 8px;
  padding: 0 8px;
  font-size: 12px;
  font-family: inherit;
}

.icon-btn {
  &.active {
    background: var(--grey-100);
  }
}

.tab-btn.non-participant {
  opacity: 0.4;
}

.tab-btn {
  background: var(--grey-50);
  padding: 4px 10px;

  &:hover {
    background: var(--grey-100);
  }

  &:active {
    background: var(--grey-200);
  }

  &.active {
    background: var(--grey-100);
    font-weight: 700;
    outline-offset: 2px;
    outline: 2px solid var(--grey-900);
  }
}


.stats-filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid var(--grey-25);
}

.summary-content {
  flex: 1;
  overflow: auto;
  padding: 12px;
}

.summary-view-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}


.empty-text {
  text-align: center;
  color: var(--grey-400);
}

.match-info-editor {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.match-info-editor h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: var(--grey-900);
  font-size: 18px;
}

.info-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-form label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label-text {
  font-size: 13px;
  font-weight: 600;
  color: var(--grey-600);
}

.info-form input,
.info-form select {
  border: 1px solid var(--grey-75);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: inherit;
}

.info-form input:focus,
.info-form select:focus {
  outline: none;
  border-color: var(--red-accent);
  box-shadow: 0 0 0 3px rgba(0, 94, 255, 0.1);
}
</style>
